import { useEffect, useState, useCallback } from 'react';
import useAuth from '../../../hooks/useAuth';
import axiosInstance from '../../../api/axios';
import { Box, Card, Tab, Tabs, Typography } from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';
import { useTheme } from '@mui/material/styles';
import Loading from '../../../components/common/loading/Loading';
import Table from './Table';
import { notifySuccess, notifyError } from "../../../components/common/toast/ToastFunc";

const CHECKING = "Checking";
const SAVINGS = "Savings";

const AccountSummary = () => {

  const { token } = useAuth();

  const [data, setData] = useState(null);
  const [tabValue, setTabValue] = useState('1');
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [reFetch, setReFetch] = useState(false);

  const theme = useTheme();

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const formatData = useCallback((inputData) => {
    try {

      const holderName = inputData[1].holder_name;

      const convertedData = {
        account_holder: holderName,
        checking: [],
        savings: []
      };
      inputData[0].forEach(account => {
        if (account.type === "CHECKING") {
          convertedData.checking.push({
            account_no: account.id.replace(/-/g, ''),
            account_balance: account.balance,
            routing_no: "074000010"
          });
        } else if (account.type === "SAVINGS") {
          convertedData.savings.push({
            account_no: account.id.replace(/-/g, ''),
            account_balance: account.balance,
            routing_no: "074000010"
          });
        }
      });

      setData(convertedData);
    } catch (error) {
      notifyError("Something went wrong");
    }
  }, []);

  const addAccount = useCallback(async (account_type) => {
    try {
      setActionLoading(true);
      const response = await axiosInstance.post('/customer/account-summary/add-account', { account_type });
      notifySuccess(response?.data?.message || "Success");
      setReFetch(!reFetch);
    } catch (error) {
      notifyError(error.message || "Something went wrong");
    } finally {
      setActionLoading(false);
    }
  }, [reFetch]);

  const deleteAccount = useCallback(async (account_no) => {
    try {
      setActionLoading(true);
      const response = await axiosInstance.post('/customer/account-summary/delete-account', { account_id: account_no });
      notifySuccess(response?.data?.message || "Success");
      setReFetch(!reFetch);
    } catch (error) {
      notifyError(error.message || "Something went wrong");
    } finally {
      setActionLoading(false);
    }
  }, [reFetch]);

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true);
        setData(null);
        const response = await axiosInstance.get('/customer/account-summary/get-data');
        formatData(response.data.data);
      } catch (error) {
        notifyError(error.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    }
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reFetch, token])

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", my: 3 }}>
        <Typography variant="h5" sx={{ mb: 2, color: theme.mainColor }}>Account Summary</Typography>
        {loading && <Box mt={5}><Loading /></Box>}
        {data &&
          <Card sx={{ width: 1200, borderRadius: "7px" }}>
            <TabContext value={tabValue} >
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tabValue} onChange={handleTabChange} centered variant="fullWidth"
                  TabIndicatorProps={{
                    style: {
                      backgroundColor: theme.mainColor
                    }
                  }}
                >
                  <Tab label={<Typography sx={{ fontSize: 17, fontWeight: "bold", color: theme.mainColor }}>{CHECKING}</Typography>} value="1" disabled={actionLoading} />
                  <Tab label={<Typography sx={{ fontSize: 17, fontWeight: "bold", color: theme.mainColor }}>{SAVINGS}</Typography>} value="2" disabled={actionLoading} />
                </Tabs>
              </Box>
              <TabPanel value="1">
                <Typography sx={{ fontSize: 22, fontWeight: "bold", color: theme.mainColor, mb: 2 }}>Account Holder: {data["account_holder"]}</Typography>
                <Table data={data[CHECKING.toLowerCase()]} addAccount={addAccount} deleteAccount={deleteAccount} actionLoading={actionLoading} type={CHECKING} />
              </TabPanel>
              <TabPanel value="2">
                <Typography sx={{ fontSize: 22, fontWeight: "bold", color: theme.mainColor, mb: 2 }}>Account Holder: {data["account_holder"]}</Typography>
                <Table data={data[SAVINGS.toLowerCase()]} addAccount={addAccount} deleteAccount={deleteAccount} actionLoading={actionLoading} type={SAVINGS} />
              </TabPanel>
            </TabContext>
          </Card>
        }
      </Box>
    </>
  );
}

export default AccountSummary;